// The last step of rendering: DOMPurify with a strict configuration on a
// private instance, so its hooks never reach another DOMPurify user. Ported
// from today's pages (src/markdown/sanitize.ts), without what these pages do
// not render: HTML plans, rewritten URLs. The task-list box is the one form
// control a text may carry, drawn disabled. No button is allowed: the view
// adds its own to the DOM, never through here.
import DOMPurify, { type Config, type DOMPurify as Purifier } from 'dompurify'

const CONFIG: Config = {
  // HTML only: no inline SVG or MathML.
  USE_PROFILES: { html: true },
  FORBID_TAGS: [
    'style', 'link', 'meta', 'base', 'template', 'noscript',
    'iframe', 'frame', 'frameset', 'object', 'embed', 'portal',
    'form', 'button', 'textarea', 'select', 'option', 'optgroup', 'datalist', 'dialog',
    'marquee', 'blink',
  ],
  FORBID_ATTR: [
    'style', 'srcset', 'srcdoc', 'ping', 'autofocus', 'background',
    'action', 'method', 'form', 'formaction', 'formmethod', 'formtarget', 'formenctype', 'formnovalidate',
    'popover', 'popovertarget', 'popovertargetaction',
  ],
  // http(s), mail and phone links, and relative URLs; nothing else.
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  // Every id and name becomes `user-content-…`, so a text can never clobber
  // a DOM global or collide with the app's own ids.
  SANITIZE_NAMED_PROPS: true,
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: true,
}

/** The prefix DOMPurify gives every id and name it keeps. */
export const USER_CONTENT_PREFIX = 'user-content-'

const IMAGE_DATA_URL = /^data:image\/(?:png|jpe?g|gif|webp)[;,]/i
const URL_ATTRIBUTES = new Set(['src', 'href', 'xlink:href', 'poster', 'cite', 'action', 'formaction', 'data'])
const EXTERNAL_URL = /^(?:[a-z][a-z0-9+.-]*:)?\/\//i

let instance: Purifier | null = null

function purifier(): Purifier {
  if (instance) return instance
  const purify = DOMPurify(window)
  purify.setConfig(CONFIG)

  // Task-list checkboxes are the only form control a text may carry.
  purify.addHook('uponSanitizeElement', (node, event) => {
    if (event.tagName !== 'input') return
    const type = (node as Element).getAttribute?.('type')
    if (type?.trim().toLowerCase() !== 'checkbox') (node as Element).remove()
  })

  // data: URLs only as raster images; never SVG, HTML or scripts.
  purify.addHook('uponSanitizeAttribute', (node, event) => {
    if (!URL_ATTRIBUTES.has(event.attrName)) return
    const value = event.attrValue.replace(/[\u0000- ]/g, '')
    if (!/^data:/i.test(value)) return
    if (!(node.nodeName === 'IMG' && event.attrName === 'src' && IMAGE_DATA_URL.test(value))) event.keepAttr = false
  })

  purify.addHook('afterSanitizeAttributes', (node) => {
    if (node.nodeName === 'A' && EXTERNAL_URL.test(node.getAttribute('href') ?? '')) {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
    } else if (node.nodeName === 'INPUT') {
      node.setAttribute('disabled', '')
      node.removeAttribute('name')
      node.removeAttribute('value')
    } else if (node.nodeName === 'IMG') {
      node.setAttribute('loading', 'lazy')
      node.setAttribute('decoding', 'async')
      node.setAttribute('referrerpolicy', 'no-referrer')
    }
  })

  instance = purify
  return purify
}

/** Sanitises rendered HTML for insertion into the app's DOM. */
export function sanitizeHtml(html: string): string {
  return purifier().sanitize(html)
}
